import React, { useState, useEffect, useCallback } from "react";
import { Plus, RefreshCw, ChevronDown, Building2, ArrowUpDown, RotateCcw, FileCheck, CheckCircle, XCircle } from "lucide-react";
import { Card, PageHeader, Btn, Input, Select, Field, Modal, Table, Spinner, ErrBox, Badge } from "../components/UI";
import { FilterBar, buildQS, initFilters } from "../components/FilterBar";
import { fmtRp, fmtDate } from "../utils/format";
import { bendaharaAPI, masterAPI } from "../utils/api";

const CoaBadge = ({ kode, nama, jenis, program_kode, kegiatan_kode, kro_kode, ro_kode, ro_nama }) => (
  <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
      <span style={{fontFamily:"monospace",fontWeight:700,color:"#2563EB",fontSize:12}}>{kode || '-'}</span>
      <span style={{fontSize:10,padding:"1px 5px",borderRadius:4,background:jenis==="BELANJA"?"#FEE2E2":jenis==="PENDAPATAN"?"#D1FAE5":"#E0E7FF",color:jenis==="BELANJA"?"#991B1B":jenis==="PENDAPATAN"?"#065F46":"#3730A3"}}>{jenis || 'LAINNYA'}</span>
    </div>
    <span style={{fontSize:11,color:"#374151"}}>{nama || '-'}</span>
    <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginTop:2 }}>
      {program_kode && <span style={{fontSize:9,fontFamily:"monospace",background:"#EEF2FF",padding:"1px 4px",borderRadius:3,color:"#4338CA"}}>{program_kode}</span>}
      {kegiatan_kode && <span style={{fontSize:9,fontFamily:"monospace",background:"#FEF3C7",padding:"1px 4px",borderRadius:3,color:"#92400E"}}>{kegiatan_kode}</span>}
      {kro_kode && <span style={{fontSize:9,fontFamily:"monospace",background:"#DCFCE7",padding:"1px 4px",borderRadius:3,color:"#166534"}}>{kro_kode}</span>}
      {ro_kode && <span style={{fontSize:9,fontFamily:"monospace",background:"#EDE9FE",padding:"1px 4px",borderRadius:3,color:"#5B21B6"}}>{ro_kode}</span>}
    </div>
    {ro_nama && <span style={{fontSize:10,color:"#6B7280",fontStyle:"italic"}}>{ro_nama}</span>}
  </div>
);

const BendaharaPage = () => {
  const [activeTab, setActiveTab] = useState("jurnal");
  const [data, setData]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [filters, setFilters]     = useState({ tahun: new Date().getFullYear() });
  const [search, setSearch]       = useState("");
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [err, setErr]             = useState("");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ tanggal: "", no_bukti: "", akun_id: "", coa_detail_id: "", uraian: "", debit: "", kredit: "", tahun_anggaran: new Date().getFullYear() });
  const [refAkun, setRefAkun]     = useState([]);
  const [refProgram, setRefProgram] = useState([]);
  const [refKegiatan, setRefKegiatan] = useState([]);
  const [refKro, setRefKro]       = useState([]);
  const [refCoaDetail, setRefCoaDetail] = useState([]);
  const [selectedAkun, setSelectedAkun] = useState(null);
  // buku kas
  const [bkData, setBkData]       = useState([]);
  const [bkTotal, setBkTotal]    = useState(0);
  const [bkFilters, setBkFilters] = useState({ rekening_id: "", dari: "", sampai: "" });
  const [bkLoading, setBkLoading] = useState(false);
  const [bkErr, setBkErr]         = useState("");
  const [posisiKas, setPosisiKas] = useState(null);
  const [refRekening, setRefRekening] = useState([]);
const [rebuilding, setRebuilding] = useState(false);

  // ── REKONSILIASI ───────────────────────────────────────
  const [rekData, setRekData]     = useState([]);
  const [rekTotal, setRekTotal]   = useState(0);
  const [rekFilters, setRekFilters] = useState({ rekening_id: "", bulan: "", tahun: new Date().getFullYear() });
  const [rekLoading, setRekLoading] = useState(false);
  const [rekErr, setRekErr]       = useState("");
  const [showRekModal, setShowRekModal] = useState(false);
  const [rekForm, setRekForm] = useState({ rekening_id: "", bulan: "", tahun: new Date().getFullYear(), saldo_awal: "", total_masuk: "", total_keluar: "", saldo_stlh_rekonsiliasi: "", tanggal_rekonsiliasi: "" });
  const [savingRek, setSavingRek] = useState(false);
  const [rekApproval, setRekApproval] = useState(null);

  // ── BUKU KAS ───────────────────────────────────────────
  const loadPosisiKas = async () => {
    try { const r = await bendaharaAPI.getPosisiKas(); setPosisiKas(r.data); } catch(e) { console.error(e); }
  };

  const loadRekening = async () => {
    try { const r = await masterAPI.getRekening(); setRefRekening((r.data||[]).map(x => ({ value: x.id, label: `${x.nama_bank} - ${x.no_rekening}` }))); } catch(e) { console.error(e); }
  };

  const loadBukuKas = useCallback(async () => {
    setBkLoading(true); setBkErr("");
    try {
      const params = new URLSearchParams();
      if (bkFilters.rekening_id) params.append('rekening_id', bkFilters.rekening_id);
      if (bkFilters.dari) params.append('dari', bkFilters.dari);
      if (bkFilters.sampai) params.append('sampai', bkFilters.sampai);
      const qs = params.toString() ? '?' + params.toString() : '';
      const r = await bendaharaAPI.getBukuKas(qs);
      setBkData(r.data || []);
      setBkTotal(r.pagination?.total || 0);
    } catch(e) { setBkErr(e.message); }
    finally { setBkLoading(false); }
  }, [bkFilters]);

  const handleRebuild = async (tanggal, rekening_id) => {
    if (!tanggal || !rekening_id) return;
    setRebuilding(true);
    try {
      await bendaharaAPI.rebuildBukuKas({ tanggal, rekening_id });
      loadBukuKas();
      loadPosisiKas();
    } catch(e) { alert("Gagal rebuild: " + e.message); }
    finally { setRebuilding(false); }
  };

  useEffect(() => { loadPosisiKas(); loadRekening(); }, []);
  useEffect(() => { if (activeTab === 'buku-kas') loadBukuKas(); }, [activeTab, loadBukuKas]);

  const loadJurnal = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const qs = buildQS(filters, search);
      const res = await bendaharaAPI.getJurnal(qs);
      setData(res.data || []);
      setTotal(res.pagination?.total || 0);
    } catch(e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [filters, search]);

  const loadRefs = async () => {
    try {
      const [akun, opts, cd] = await Promise.all([
        masterAPI.getCoaAkun(),
        masterAPI.getCoaOptions(),
        masterAPI.getCoaDetailAkun(),
      ]);
      setRefAkun((akun.data||[]).map(x => ({ value: x.id, label: `${x.kode} - ${x.nama}`, jenis: x.jenis, program_kode: x.program_kode, kegiatan_kode: x.kegiatan_kode, kro_kode: x.kro_kode })));
      setRefProgram((opts.data?.program||[]).map(p => ({ value: p.id, label: `${p.kode} - ${p.nama}` })));
      setRefKegiatan((opts.data?.kegiatan||[]).map(k => ({ value: k.id, label: `${k.kode} - ${k.nama}` })));
      setRefKro((opts.data?.kro||[]).map(k => ({ value: k.id, label: `${k.kode} - ${k.nama}` })));
      setRefCoaDetail((cd.data||[]).map(x => ({ value: x.id, label: `${x.kode} - ${x.nama}`, akun_id: x.akun_id })));
    } catch(e) { console.error(e); }
  };

  useEffect(() => { loadJurnal(); }, [loadJurnal]);
  useEffect(() => { loadRefs(); }, []);

  const loadRekonsiliasi = useCallback(async () => {
    setRekLoading(true); setRekErr("");
    try {
      const params = new URLSearchParams();
      if (rekFilters.rekening_id) params.append('rekening_id', rekFilters.rekening_id);
      if (rekFilters.bulan) params.append('bulan', rekFilters.bulan);
      if (rekFilters.tahun) params.append('tahun', rekFilters.tahun);
      const qs = params.toString() ? '?' + params.toString() : '';
      const r = await bendaharaAPI.getRekonsiliasi(qs);
      setRekData(r.data || []);
      setRekTotal(r.pagination?.total || 0);
    } catch(e) { setRekErr(e.message); }
    finally { setRekLoading(false); }
  }, [rekFilters]);

  const handleSaveRek = async () => {
    if (!rekForm.rekening_id || !rekForm.bulan || !rekForm.tahun) return;
    setSavingRek(true);
    try {
      await bendaharaAPI.createRekonsiliasi({
        rekening_id: rekForm.rekening_id,
        bulan: Number(rekForm.bulan),
        tahun: Number(rekForm.tahun),
        saldo_awal: Number(rekForm.saldo_awal || 0),
        total_masuk: Number(rekForm.total_masuk || 0),
        total_keluar: Number(rekForm.total_keluar || 0),
        saldo_stlh_rekonsiliasi: Number(rekForm.saldo_stlh_rekonsiliasi || 0),
        tanggal_rekonsiliasi: rekForm.tanggal_rekonsiliasi || null,
      });
      setShowRekModal(false);
      setRekForm({ rekening_id: "", bulan: "", tahun: new Date().getFullYear(), saldo_awal: "", total_masuk: "", total_keluar: "", saldo_stlh_rekonsiliasi: "", tanggal_rekonsiliasi: "" });
      loadRekonsiliasi();
    } catch(e) { alert("Gagal menyimpan: " + e.message); }
    finally { setSavingRek(false); }
  };

  const handleSync = async () => {
    setLoading(true);
    try {
      await bendaharaAPI.syncJurnal();
      loadJurnal();
    } catch(e) { alert("Gagal sync: " + e.message); }
    finally { setLoading(false); }
  };

  const handleApproveRek = async (id) => {
    if (!confirm("Setujui rekonsiliasi ini?")) return;
    try { await bendaharaAPI.approveRekonsiliasi(id); loadRekonsiliasi(); }
    catch(e) { alert("Gagal approve: " + e.message); }
  };

  useEffect(() => { if (activeTab === 'rekonsiliasi') loadRekonsiliasi(); }, [activeTab, loadRekonsiliasi]);

  const tabs = [
    { key: "jurnal", label: "Jurnal Umum" },
    { key: "buku-kas", label: "Buku Kas" },
    { key: "rekonsiliasi", label: "Rekonsiliasi" },
  ];

  return (
    <div>
      <PageHeader title="Bendahara" subtitle="Manajemen keuangan dan akunting - Jurnal Umum, Buku Kas, Rekonsiliasi"
        action={
          <div style={{ display:"flex", gap:8 }}>
            <Btn variant="secondary" icon={<RefreshCw size={14}/>} onClick={handleSync}>Sync Jurnal</Btn>
            <Btn icon={<Plus size={14}/>} onClick={()=>setShowModal(true)}>Buat Jurnal</Btn>
          </div>
        }
      />
      <div style={{ display:"flex", gap:4, marginBottom:20, borderBottom:"1px solid #E5E7EB" }}>
        {tabs.map(t => (
          <button key={t.key} onClick={()=>setActiveTab(t.key)} style={{
            padding:"8px 16px", border:"none", background:"none", cursor:"pointer",
            fontSize:13, fontWeight:activeTab===t.key?600:400, color:activeTab===t.key?"#2563EB":"#6B7280",
            borderBottom:activeTab===t.key?"2px solid #2563EB":"2px solid transparent", marginBottom:-1
          }}>{t.label}</button>
        ))}
      </div>

      {activeTab === "jurnal" && (
        <>
          <FilterBar filters={filters} onChange={setFilters} search={search} onSearch={setSearch}
            searchPlaceholder="Cari no jurnal, no bukti, uraian…"/>
          <Card>
            {err && <div style={{marginBottom:12}}><ErrBox msg={err} onRetry={()=>loadJurnal()}/></div>}
            {loading ? <Spinner/> : (
              <>
                <div style={{ marginBottom:12, fontSize:12, color:"#6B7280" }}>Total: {total} transaksi</div>
                <Table pageSize={15} cols={[
                  { key:"no_jurnal", label:"No Jurnal", nowrap:true, render:v=><span style={{fontFamily:"monospace",fontWeight:700,color:"#2563EB"}}>{v}</span> },
                  { key:"tanggal", label:"Tanggal", render:v=>fmtDate(v) },
                  { key:"no_bukti", label:"No Bukti", render:v=>v||<span style={{color:"#9CA3AF"}}>-</span> },
                  { key:"kode_akun", label:"Akun / COA Detail", render:(v,r)=>(
                    <CoaBadge
                      kode={r.kode_akun} nama={r.nama_akun} jenis={r.akun_jenis}
                      program_kode={r.program_kode} kegiatan_kode={r.kegiatan_kode}
                      kro_kode={r.kro_kode} ro_kode={r.ro_kode} ro_nama={r.ro_nama}
                    />
                  )},
                  { key:"uraian", label:"Uraian" },
                  { key:"debit", label:"Debit", align:"right", render:v=>v>0?<span style={{fontFamily:"monospace",color:"#DC2626"}}>{fmtRp(Number(v))}</span>:<span style={{color:"#9CA3AF"}}>-</span> },
                  { key:"kredit", label:"Kredit", align:"right", render:v=>v>0?<span style={{fontFamily:"monospace",color:"#059669"}}>{fmtRp(Number(v))}</span>:<span style={{color:"#9CA3AF"}}>-</span> },
                  { key:"dibuat_oleh", label:"Dibuat Oleh", render:v=>v||<span style={{color:"#9CA3AF"}}>-</span> },
                ]} data={data}/>
              </>
            )}
          </Card>
        </>
      )}

      {activeTab === "buku-kas" && (
        <>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:14, marginBottom:20 }}>
            {posisiKas?.rekening?.map(r => (
              <Card key={r.id} style={{ padding:"16px 20px" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                  <Building2 size={16} color="#2563EB"/>
                  <div>
                    <div style={{ fontSize:12, fontWeight:700, color:"#111827" }}>{r.nama_bank}</div>
                    <div style={{ fontSize:10, color:"#6B7280", fontFamily:"monospace" }}>{r.no_rekening}</div>
                  </div>
                </div>
                <div style={{ fontSize:22, fontWeight:800, color:"#111827", fontFamily:"monospace", marginBottom:4 }}>
                  {fmtRp(Number(r.saldo_akhir || 0))}
                </div>
                <div style={{ display:"flex", gap:16, fontSize:11 }}>
                  <span style={{ color:"#059669" }}>Masuk {fmtRp(Number(r.total_masuk || 0))}</span>
                  <span style={{ color:"#DC2626" }}>Keluar {fmtRp(Number(r.total_keluar || 0))}</span>
                </div>
                <div style={{ marginTop:10, display:"flex", gap:6 }}>
                  <Input type="date" style={{ fontSize:11, padding:"4px 8px" }} value={bkFilters._rb_tgl || ""} onChange={e => setBkFilters({...bkFilters, _rb_tgl: e.target.value})} title="Tanggal rebuild"/>
                  <Btn size="sm" variant="secondary" icon={<RotateCcw size={12}/>} onClick={() => handleRebuild(bkFilters._rb_tgl, r.id)} disabled={rebuilding || !bkFilters._rb_tgl} title="Rebuild">Rebuild</Btn>
                </div>
              </Card>
            ))}
          </div>
          <Card>
            <div style={{ display:"flex", gap:12, marginBottom:16, flexWrap:"wrap", alignItems:"flex-end" }}>
              <div>
                <label style={{ fontSize:11, fontWeight:600, color:"#64748B", display:"block", marginBottom:4 }}>Rekening Bank</label>
                <Select value={bkFilters.rekening_id} onChange={e=>setBkFilters({...bkFilters, rekening_id:e.target.value})} options={[{value:"",label:"Semua Rekening"}, ...refRekening]} style={{ minWidth:200 }}/>
              </div>
              <div>
                <label style={{ fontSize:11, fontWeight:600, color:"#64748B", display:"block", marginBottom:4 }}>Dari Tanggal</label>
                <Input type="date" value={bkFilters.dari} onChange={e=>setBkFilters({...bkFilters, dari:e.target.value})} style={{ width:150 }}/>
              </div>
              <div>
                <label style={{ fontSize:11, fontWeight:600, color:"#64748B", display:"block", marginBottom:4 }}>Sampai Tanggal</label>
                <Input type="date" value={bkFilters.sampai} onChange={e=>setBkFilters({...bkFilters, sampai:e.target.value})} style={{ width:150 }}/>
              </div>
              <Btn variant="secondary" icon={<ArrowUpDown size={13}/>} onClick={loadBukuKas}>Tampilkan</Btn>
            </div>
            {bkErr && <div style={{marginBottom:12}}><ErrBox msg={bkErr} onRetry={loadBukuKas}/></div>}
            {bkLoading ? <Spinner/> : (
              <>
                <div style={{ marginBottom:10, fontSize:12, color:"#6B7280" }}>
                  Total: {bkTotal} entri
                </div>
                <Table pageSize={20} cols={[
                  { key:"tanggal", label:"Tanggal", render:v=>fmtDate(v) },
                  { key:"nama_bank", label:"Rekening", render:(v,r)=>r.nama_bank||<span style={{color:"#9CA3AF"}}>-</span> },
                  { key:"no_rekening", label:"No Rekening", render:v=>v||<span style={{color:"#9CA3AF"}}>-</span> },
                  { key:"saldo_awal", label:"Saldo Awal", align:"right", render:v=><span style={{fontFamily:"monospace"}}>{fmtRp(Number(v||0))}</span> },
                  { key:"total_masuk", label:"Debit (Masuk)", align:"right", render:v=><span style={{fontFamily:"monospace",color:"#059669"}}>{fmtRp(Number(v||0))}</span> },
                  { key:"total_keluar", label:"Kredit (Keluar)", align:"right", render:v=><span style={{fontFamily:"monospace",color:"#DC2626"}}>{fmtRp(Number(v||0))}</span> },
                  { key:"saldo_akhir", label:"Saldo Akhir", align:"right", render:v=><span style={{fontFamily:"monospace",fontWeight:700}}>{fmtRp(Number(v||0))}</span> },
                ]} data={bkData}/>
              </>
            )}
          </Card>
        </>
      )}

      {activeTab === "rekonsiliasi" && (
        <>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:16 }}>
            <div style={{ display:"flex", gap:12, alignItems:"flex-end", flexWrap:"wrap" }}>
              <div>
                <label style={{ fontSize:11, fontWeight:600, color:"#64748B", display:"block", marginBottom:4 }}>Rekening Bank</label>
                <Select value={rekFilters.rekening_id} onChange={e=>setRekFilters({...rekFilters, rekening_id:e.target.value})} options={[{value:"",label:"Semua Rekening"}, ...refRekening]} style={{ minWidth:200 }}/>
              </div>
              <div>
                <label style={{ fontSize:11, fontWeight:600, color:"#64748B", display:"block", marginBottom:4 }}>Bulan</label>
                <Select value={rekFilters.bulan} onChange={e=>setRekFilters({...rekFilters, bulan:e.target.value})} options={[{value:"",label:"Semua Bulan"}, ...Array.from({length:12},(_,i)=>({value:String(i+1), label:new Date(0,i+1).toLocaleString('id',{month:'long'})}))]} style={{ minWidth:140 }}/>
              </div>
              <div>
                <label style={{ fontSize:11, fontWeight:600, color:"#64748B", display:"block", marginBottom:4 }}>Tahun</label>
                <Input type="number" value={rekFilters.tahun} onChange={e=>setRekFilters({...rekFilters, tahun:e.target.value})} style={{ width:100 }}/>
              </div>
              <Btn variant="secondary" icon={<ArrowUpDown size={13}/>} onClick={loadRekonsiliasi}>Tampilkan</Btn>
            </div>
            <Btn icon={<Plus size={14}/>} onClick={()=>setShowRekModal(true)}>Buat Rekonsiliasi</Btn>
          </div>
          {rekErr && <div style={{marginBottom:12}}><ErrBox msg={rekErr} onRetry={loadRekonsiliasi}/></div>}
          {rekLoading ? <Spinner/> : (
            <>
              <div style={{ marginBottom:10, fontSize:12, color:"#6B7280" }}>Total: {rekTotal} entri</div>
              <Table pageSize={20} cols={[
                { key:"nama_bank", label:"Rekening", render:(v,r)=><div><div style={{fontWeight:600}}>{v||'-'}</div><div style={{fontSize:11,color:"#6B7280",fontFamily:"monospace"}}>{r.no_rekening||'-'}</div></div> },
                { key:"bulan", label:"Periode", render:(v,r)=><span style={{fontFamily:"monospace"}}>{String(v).padStart(2,'0')}/{r.tahun}</span> },
                { key:"saldo_awal", label:"Saldo Buku", align:"right", render:v=><span style={{fontFamily:"monospace"}}>{fmtRp(Number(v||0))}</span> },
                { key:"total_masuk", label:"Debit", align:"right", render:v=><span style={{fontFamily:"monospace",color:"#059669"}}>{fmtRp(Number(v||0))}</span> },
                { key:"total_keluar", label:"Kredit", align:"right", render:v=><span style={{fontFamily:"monospace",color:"#DC2626"}}>{fmtRp(Number(v||0))}</span> },
                { key:"saldo_stlh_rekonsiliasi", label:"Saldo Bank", align:"right", render:v=><span style={{fontFamily:"monospace",fontWeight:700}}>{fmtRp(Number(v||0))}</span> },
                { key:"tanggal_rekonsiliasi", label:"Tgl Reconciliasi", render:v=>v?fmtDate(v):<span style={{color:"#9CA3AF"}}>-</span> },
                { key:"disetujui_oleh", label:"Status", render:(v,r)=>(
                  r.disetujui_oleh ? <span style={{ display:"flex", alignItems:"center", gap:4, color:"#059669", fontSize:12 }}><CheckCircle size={14}/> Disetujui</span>
                  : r.tanggal_rekonsiliasi ? <span style={{ display:"flex", alignItems:"center", gap:4, color:"#D97706", fontSize:12 }}><XCircle size={14}/> Pending</span>
                  : <span style={{ color:"#9CA3AF", fontSize:12 }}>Draft</span>
                )},
                { key:"disetujui_oleh", label:"Aksi", render:(v,r)=>(
                  r.disetujui_oleh ? null
                  : <Btn size="sm" variant="secondary" icon={<CheckCircle size={12}/>} onClick={()=>handleApproveRek(r.id)}>Approve</Btn>
                )},
              ]} data={rekData}/>
            </>
          )}
        </>
      )}

      {showRekModal && (
        <Modal title="Buat Rekonsiliasi Bank" onClose={()=>setShowRekModal(false)}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            <Field label="Rekening Bank" required>
              <Select value={rekForm.rekening_id} onChange={e=>setRekForm({...rekForm, rekening_id:e.target.value})} options={refRekening} placeholder="Pilih rekening…"/>
            </Field>
            <Field label="Tahun" required>
              <Input type="number" value={rekForm.tahun} onChange={e=>setRekForm({...rekForm, tahun:e.target.value})}/>
            </Field>
            <Field label="Bulan" required>
              <Select value={rekForm.bulan} onChange={e=>setRekForm({...rekForm, bulan:e.target.value})} options={Array.from({length:12},(_,i)=>({value:String(i+1), label:new Date(0,i+1).toLocaleString('id',{month:'long'})}))}/>
            </Field>
            <Field label="Tanggal Rekonsiliasi">
              <Input type="date" value={rekForm.tanggal_rekonsiliasi} onChange={e=>setRekForm({...rekForm, tanggal_rekonsiliasi:e.target.value})}/>
            </Field>
            <Field label="Saldo Buku (Saldo Awal)" style={{gridColumn:"1/-1"}}>
              <Input type="number" value={rekForm.saldo_awal} onChange={e=>setRekForm({...rekForm, saldo_awal:e.target.value})} placeholder="0"/>
            </Field>
            <Field label="Total Debit (Masuk)">
              <Input type="number" value={rekForm.total_masuk} onChange={e=>setRekForm({...rekForm, total_masuk:e.target.value})} placeholder="0"/>
            </Field>
            <Field label="Total Kredit (Keluar)">
              <Input type="number" value={rekForm.total_keluar} onChange={e=>setRekForm({...rekForm, total_keluar:e.target.value})} placeholder="0"/>
            </Field>
            <Field label="Saldo Menurut Bank" required style={{gridColumn:"1/-1"}}>
              <Input type="number" value={rekForm.saldo_stlh_rekonsiliasi} onChange={e=>setRekForm({...rekForm, saldo_stlh_rekonsiliasi:e.target.value})} placeholder="0"/>
            </Field>
          </div>
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:20, paddingTop:16, borderTop:"1px solid #E5E7EB" }}>
            <Btn variant="secondary" onClick={()=>setShowRekModal(false)}>Batal</Btn>
            <Btn onClick={handleSaveRek} disabled={savingRek}>{savingRek?"Menyimpan…":"Simpan"}</Btn>
          </div>
        </Modal>
      )}

      {showModal && (
        <Modal title="Buat Jurnal Umum" onClose={()=>setShowModal(false)} wide>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            <Field label="Tanggal" required>
              <Input type="date" value={form.tanggal} onChange={e=>setForm({...form,tanggal:e.target.value})}/>
            </Field>
            <Field label="Tahun Anggaran">
              <Input type="number" value={form.tahun_anggaran} onChange={e=>setForm({...form,tahun_anggaran:e.target.value})}/>
            </Field>
            <Field label="No Bukti" style={{gridColumn:"1/-1"}}>
              <Input value={form.no_bukti} onChange={e=>setForm({...form,no_bukti:e.target.value})} placeholder="Opsional"/>
            </Field>
            <Field label="Akun MA" required style={{gridColumn:"1/-1"}}>
              <Select value={form.akun_id} onChange={e=>handleAkunChange(e.target.value)} options={refAkun} placeholder="Pilih Akun MA…"/>
            </Field>
            {form.akun_id && (
              <>
                <Field label="Detail COA" required style={{gridColumn:"1/-1"}}>
                  <Select value={form.coa_detail_id} onChange={e=>setForm({...form,coa_detail_id:e.target.value})} options={filteredCoaDetail} placeholder="Pilih Detail COA…"/>
                  {selectedCoaDetail && (
                    <div style={{ marginTop:8, padding:8, background:"#EFF6FF", borderRadius:8, fontSize:12 }}>
                      <span style={{fontFamily:"monospace",fontWeight:700,color:"#2563EB"}}>{selectedCoaDetail.label}</span>
                    </div>
                  )}
                </Field>
              </>
            )}
            {selectedAkun && (
              <div style={{gridColumn:"1/-1", display:"flex", flexWrap:"wrap", gap:6 }}>
                <span style={{fontSize:9,fontFamily:"monospace",background:"#EEF2FF",padding:"2px 6px",borderRadius:4,color:"#4338CA"}}>{selectedAkun.program_kode || '-'}</span>
                <span style={{fontSize:9,fontFamily:"monospace",background:"#FEF3C7",padding:"2px 6px",borderRadius:4,color:"#92400E"}}>{selectedAkun.kegiatan_kode || '-'}</span>
                <span style={{fontSize:9,fontFamily:"monospace",background:"#DCFCE7",padding:"2px 6px",borderRadius:4,color:"#166534"}}>{selectedAkun.kro_kode || '-'}</span>
                <span style={{fontSize:10,padding:"2px 6px",borderRadius:4,background:selectedAkun.jenis==="BELANJA"?"#FEE2E2":"#D1FAE5",color:selectedAkun.jenis==="BELANJA"?"#991B1B":"#065F46"}}>{selectedAkun.jenis}</span>
              </div>
            )}
            <Field label="Uraian" style={{gridColumn:"1/-1"}}>
              <Input value={form.uraian} onChange={e=>setForm({...form,uraian:e.target.value})} placeholder="Deskripsi transaksi"/>
            </Field>
            <Field label="Jumlah (Debit)" required>
              <Input type="number" value={form.debit} onChange={e=>setForm({...form,debit:e.target.value})} placeholder="0"/>
            </Field>
            <Field label="Jumlah (Kredit)" required>
              <Input type="number" value={form.kredit} onChange={e=>setForm({...form,kredit:e.target.value})} placeholder="0"/>
            </Field>
          </div>
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:20, paddingTop:16, borderTop:"1px solid #E5E7EB" }}>
            <Btn variant="secondary" onClick={()=>setShowModal(false)}>Batal</Btn>
            <Btn onClick={handleSave} disabled={saving}>{saving?"Menyimpan…":"Simpan Jurnal"}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default BendaharaPage;
